import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Building2,
  CheckCircle,
  XCircle,
  Clock,
  FileCheck,
  FileX,
  ImageOff,
  Play,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import type { LeadResult, LeadSortBy, SortOrder } from '../../types/leads';
import { useLeadScreening } from '../../hooks/useLeadScreening';

interface LeadResultsTableProps {
  leads: LeadResult[];
  isLoading: boolean;
  page: number;
  pages: number;
  total: number;
  sortBy: LeadSortBy;
  sortOrder: SortOrder;
  selectedIds: Set<string>;
  onSortChange: (sortBy: LeadSortBy, sortOrder: SortOrder) => void;
  onPageChange: (page: number) => void;
  onSelectionChange: (selectedIds: Set<string>) => void;
  /** Maximum number of properties that can be selected (for comparison) */
  maxSelection?: number;
  /** Whether more properties can be added to selection */
  canAddMore?: boolean;
}

interface SortableHeaderProps {
  label: string;
  sortKey: LeadSortBy;
  currentSortBy: LeadSortBy;
  currentSortOrder: SortOrder;
  onSort: (sortBy: LeadSortBy, sortOrder: SortOrder) => void;
  className?: string;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({
  label,
  sortKey,
  currentSortBy,
  currentSortOrder,
  onSort,
  className = '',
}) => {
  const isActive = currentSortBy === sortKey;
  const nextOrder: SortOrder = isActive && currentSortOrder === 'asc' ? 'desc' : 'asc';

  return (
    <th
      className={`px-4 py-3 text-left text-[10px] uppercase tracking-wider text-gold font-semibold cursor-pointer hover:text-ivory transition-colors select-none ${className}`}
      onClick={() => onSort(sortKey, nextOrder)}
    >
      <div className="flex items-center gap-1">
        {label}
        <span className="inline-flex flex-col">
          <ChevronUp
            className={`w-3 h-3 -mb-1 ${
              isActive && currentSortOrder === 'asc'
                ? 'text-gold'
                : 'text-stone-400'
            }`}
          />
          <ChevronDown
            className={`w-3 h-3 ${
              isActive && currentSortOrder === 'desc'
                ? 'text-gold'
                : 'text-stone-400'
            }`}
          />
        </span>
      </div>
    </th>
  );
};

/**
 * Tier badge component
 */
const TierBadge: React.FC<{ tier: string }> = ({ tier }) => {
  const tierColors: Record<string, string> = {
    Tier1: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50',
    Tier2: 'bg-amber-500/20 text-amber-400 border border-amber-500/50',
    Tier3: 'bg-blue-500/20 text-blue-400 border border-blue-500/50',
  };

  const color = tierColors[tier] || 'bg-slate-700 text-stone-500 border border-slate-600';
  const label = tier.replace('Tier', 'T');

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${color}`}
    >
      {label}
    </span>
  );
};

/**
 * License badge component
 */
const LicenseBadge: React.FC<{ hasLicense: boolean | null }> = ({ hasLicense }) => {
  if (hasLicense === null) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-slate-700 text-stone-500 border border-slate-600">
        <Clock className="w-3 h-3" />
        Unknown
      </span>
    );
  }

  return hasLicense ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/50">
      <FileCheck className="w-3 h-3" />
      Licensed
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-red-500/20 text-red-400 border border-red-500/50">
      <FileX className="w-3 h-3" />
      Unlicensed
    </span>
  );
};

/**
 * Screen status badge component
 */
interface ScreenStatusBadgeProps {
  status: LeadResult['screen_status'];
  isScreening?: boolean;
  errorMessage?: string;
}

const ScreenStatusBadge: React.FC<ScreenStatusBadgeProps> = ({
  status,
  isScreening = false,
  errorMessage,
}) => {
  // Show screening in progress
  if (isScreening) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-blue-500/20 text-blue-400 border border-blue-500/50">
        <Loader2 className="w-3 h-3 animate-spin" />
        Screening...
      </span>
    );
  }

  switch (status) {
    case 'screened':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-emerald-500/20 text-emerald-400 border border-emerald-500/50">
          <CheckCircle className="w-3 h-3" />
          Screened
        </span>
      );
    case 'failed':
      return (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-red-500/20 text-red-400 border border-red-500/50 cursor-help"
          title={errorMessage || 'Screening failed'}
        >
          <XCircle className="w-3 h-3" />
          Failed
        </span>
      );
    case 'pending':
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-slate-700 text-stone-500 border border-slate-600">
          <Clock className="w-3 h-3" />
          Pending
        </span>
      );
  }
};

/**
 * Thumbnail component with fallback
 */
const Thumbnail: React.FC<{ url?: string | null; address: string }> = ({
  url,
  address,
}) => {
  const [hasError, setHasError] = React.useState(false);

  if (!url || hasError) {
    return (
      <div className="w-12 h-12 bg-slate-800 rounded flex items-center justify-center">
        <ImageOff className="w-5 h-5 text-stone-400" />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt={address}
      className="w-12 h-12 rounded object-cover"
      onError={() => setHasError(true)}
    />
  );
};

/**
 * Loading skeleton row
 */
const SkeletonRow: React.FC = () => (
  <tr className="animate-pulse">
    <td className="px-4 py-3">
      <div className="w-4 h-4 bg-slate-800 rounded" />
    </td>
    <td className="px-4 py-3">
      <div className="w-12 h-12 bg-slate-800 rounded" />
    </td>
    <td className="px-4 py-3">
      <div className="h-4 bg-slate-800 rounded w-48" />
    </td>
    <td className="px-4 py-3">
      <div className="h-5 bg-slate-800 rounded w-10" />
    </td>
    <td className="px-4 py-3">
      <div className="h-4 bg-slate-800 rounded w-8" />
    </td>
    <td className="px-4 py-3">
      <div className="h-4 bg-slate-800 rounded w-14" />
    </td>
    <td className="px-4 py-3">
      <div className="h-4 bg-slate-800 rounded w-8" />
    </td>
    <td className="px-4 py-3">
      <div className="h-5 bg-slate-800 rounded w-16" />
    </td>
    <td className="px-4 py-3">
      <div className="h-5 bg-slate-800 rounded w-16" />
    </td>
    <td className="px-4 py-3">
      <div className="h-4 bg-slate-800 rounded w-20" />
    </td>
    <td className="px-4 py-3">
      <div className="h-8 bg-slate-800 rounded w-16" />
    </td>
  </tr>
);

/**
 * Empty state component
 */
const EmptyState: React.FC = () => (
  <tr>
    <td colSpan={11} className="px-4 py-16 text-center">
      <Building2 className="w-12 h-12 mx-auto text-stone-400 mb-4" />
      <h3 className="text-lg font-semibold text-ivory mb-2">
        No leads found
      </h3>
      <p className="text-stone-500 max-w-md mx-auto">
        Try adjusting your filters or search criteria to find matching properties.
      </p>
    </td>
  </tr>
);

/**
 * Format currency for display
 */
function formatCurrency(value: number | null): string {
  if (value === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format percentage for display
 */
function formatPercent(value: number | null): string {
  if (value === null) return '-';
  return `${(value * 100).toFixed(2)}%`;
}

/**
 * LeadResultsTable component
 *
 * A sortable, paginated table displaying lead search results
 * with row selection for batch operations and screening capabilities.
 */
export const LeadResultsTable: React.FC<LeadResultsTableProps> = ({
  leads,
  isLoading,
  page,
  pages,
  total,
  sortBy,
  sortOrder,
  selectedIds,
  onSortChange,
  onPageChange,
  onSelectionChange,
  maxSelection,
  canAddMore = true,
}) => {
  const navigate = useNavigate();

  // Screening functionality
  const {
    screenSingleLead,
    screenMultipleLeads,
    isScreening,
    getScreeningResult,
    isBatchScreening,
  } = useLeadScreening();

  // Handle single lead screening
  const handleScreenLead = useCallback(
    (leadId: string, event: React.MouseEvent) => {
      event.stopPropagation(); // Prevent row click navigation
      screenSingleLead(leadId);
    },
    [screenSingleLead]
  );

  // Handle batch screening of selected leads
  const handleBatchScreen = useCallback(() => {
    const leadIds = Array.from(selectedIds);
    screenMultipleLeads(leadIds);
  }, [selectedIds, screenMultipleLeads]);

  // Check if a lead is already screened (has cap_rate)
  const isAlreadyScreened = useCallback((lead: LeadResult): boolean => {
    return lead.cap_rate !== null || lead.screen_status === 'screened';
  }, []);

  // Get the effective screen status considering local screening state
  const getEffectiveScreenStatus = useCallback(
    (lead: LeadResult): LeadResult['screen_status'] => {
      const result = getScreeningResult(lead.id);
      if (result) {
        return result.success ? 'screened' : 'failed';
      }
      return lead.screen_status;
    },
    [getScreeningResult]
  );

  // Get error message for failed screening
  const getScreeningError = useCallback(
    (leadId: string): string | undefined => {
      const result = getScreeningResult(leadId);
      return result?.error_reason || result?.error;
    },
    [getScreeningResult]
  );

  // Handle select all checkbox
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        // If maxSelection is set, only select up to that limit
        const idsToSelect = maxSelection
          ? leads.slice(0, maxSelection).map((lead) => lead.id)
          : leads.map((lead) => lead.id);
        onSelectionChange(new Set(idsToSelect));
      } else {
        onSelectionChange(new Set());
      }
    },
    [leads, maxSelection, onSelectionChange]
  );

  // Handle individual row checkbox
  const handleSelectRow = useCallback(
    (id: string, checked: boolean) => {
      const newSelection = new Set(selectedIds);
      if (checked) {
        // Only add if we can add more or if it's already selected
        if (canAddMore || selectedIds.has(id)) {
          newSelection.add(id);
        }
      } else {
        newSelection.delete(id);
      }
      onSelectionChange(newSelection);
    },
    [selectedIds, canAddMore, onSelectionChange]
  );

  // Handle row click (navigate to property)
  const handleRowClick = useCallback(
    (id: string, event: React.MouseEvent) => {
      // Don't navigate if clicking on checkbox or button
      const target = event.target as HTMLElement;
      if (
        target.closest('input[type="checkbox"]') ||
        target.closest('button')
      ) {
        return;
      }
      navigate(`/property/${id}`);
    },
    [navigate]
  );

  const allSelected = leads.length > 0 && leads.every((lead) => selectedIds.has(lead.id));
  const someSelected = leads.some((lead) => selectedIds.has(lead.id));

  return (
    <div className="flex flex-col h-full">
      {/* Table container */}
      <div className="flex-1 overflow-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-slate-900 sticky top-0 z-10">
            <tr>
              {/* Select all checkbox */}
              <th className="px-4 py-3 w-12">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected && !allSelected;
                  }}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-gold focus:ring-gold focus:ring-offset-0"
                  disabled={isLoading || leads.length === 0}
                />
              </th>
              {/* Thumbnail */}
              <th className="px-4 py-3 w-16 text-[10px] uppercase tracking-wider text-gold font-semibold">
                Image
              </th>
              {/* Sortable columns */}
              <SortableHeader
                label="Address"
                sortKey="address"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSortChange}
                className="min-w-[200px]"
              />
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-gold font-semibold">
                Tier
              </th>
              <SortableHeader
                label="Units"
                sortKey="total_units"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSortChange}
              />
              <SortableHeader
                label="Cap Rate"
                sortKey="cap_rate"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSortChange}
              />
              <SortableHeader
                label="Score"
                sortKey="lead_score"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSortChange}
              />
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-gold font-semibold">
                License
              </th>
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-gold font-semibold">
                Status
              </th>
              <SortableHeader
                label="Market Value"
                sortKey="market_value"
                currentSortBy={sortBy}
                currentSortOrder={sortOrder}
                onSort={onSortChange}
              />
              {/* Actions column header */}
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-gold font-semibold w-24">
                {selectedIds.size > 0 ? (
                  <button
                    onClick={handleBatchScreen}
                    disabled={isBatchScreening}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gold bg-gold/10 border border-gold/30 rounded hover:bg-gold/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title={`Screen ${selectedIds.size} selected leads`}
                  >
                    {isBatchScreening ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Screening...
                      </>
                    ) : (
                      <>
                        <Play className="w-3 h-3" />
                        Screen ({selectedIds.size})
                      </>
                    )}
                  </button>
                ) : (
                  'Actions'
                )}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700">
            {isLoading ? (
              // Loading skeleton
              Array.from({ length: 10 }).map((_, i) => <SkeletonRow key={i} />)
            ) : leads.length === 0 ? (
              // Empty state
              <EmptyState />
            ) : (
              // Data rows
              leads.map((lead) => (
                <tr
                  key={lead.id}
                  onClick={(e) => handleRowClick(lead.id, e)}
                  className={`hover:bg-gold/5 cursor-pointer transition-colors ${
                    selectedIds.has(lead.id) ? 'bg-gold/10' : ''
                  }`}
                >
                  {/* Checkbox */}
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(lead.id)}
                      onChange={(e) => handleSelectRow(lead.id, e.target.checked)}
                      disabled={!canAddMore && !selectedIds.has(lead.id)}
                      title={!canAddMore && !selectedIds.has(lead.id) ? `Maximum ${maxSelection} properties can be selected` : undefined}
                      className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-gold focus:ring-gold focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </td>
                  {/* Thumbnail */}
                  <td className="px-4 py-3">
                    <Thumbnail url={lead.thumbnail_url} address={lead.address} />
                  </td>
                  {/* Address */}
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-ivory">
                        {lead.address}
                      </div>
                      {lead.neighborhood && (
                        <div className="text-sm text-stone-500">
                          {lead.neighborhood}
                          {lead.zip_code && ` - ${lead.zip_code}`}
                        </div>
                      )}
                    </div>
                  </td>
                  {/* Tier */}
                  <td className="px-4 py-3">
                    <TierBadge tier={lead.neighborhood_tier} />
                  </td>
                  {/* Units */}
                  <td className="px-4 py-3 text-ivory font-mono">
                    {lead.total_units}
                  </td>
                  {/* Cap Rate */}
                  <td className="px-4 py-3 text-ivory font-medium font-mono">
                    {formatPercent(lead.cap_rate)}
                  </td>
                  {/* Score */}
                  <td className="px-4 py-3">
                    {lead.lead_score !== null ? (
                      <span
                        className={`font-medium font-mono ${
                          lead.lead_score >= 70
                            ? 'text-emerald-400'
                            : lead.lead_score >= 50
                            ? 'text-amber-400'
                            : 'text-red-400'
                        }`}
                      >
                        {lead.lead_score}
                      </span>
                    ) : (
                      <span className="text-stone-500">-</span>
                    )}
                  </td>
                  {/* License */}
                  <td className="px-4 py-3">
                    <LicenseBadge hasLicense={lead.has_rental_license} />
                  </td>
                  {/* Screen Status */}
                  <td className="px-4 py-3">
                    <ScreenStatusBadge
                      status={getEffectiveScreenStatus(lead)}
                      isScreening={isScreening(lead.id)}
                      errorMessage={getScreeningError(lead.id)}
                    />
                  </td>
                  {/* Market Value */}
                  <td className="px-4 py-3 text-ivory font-mono">
                    {formatCurrency(lead.market_value)}
                  </td>
                  {/* Actions */}
                  <td className="px-4 py-3">
                    {isScreening(lead.id) ? (
                      <button
                        disabled
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-400 bg-blue-500/10 border border-blue-500/30 rounded cursor-not-allowed"
                      >
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Screening
                      </button>
                    ) : isAlreadyScreened(lead) ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded">
                        <CheckCircle className="w-3 h-3" />
                        Screened
                      </span>
                    ) : getEffectiveScreenStatus(lead) === 'failed' ? (
                      <button
                        onClick={(e) => handleScreenLead(lead.id, e)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-400 bg-red-500/10 border border-red-500/30 rounded hover:bg-red-500/20 transition-colors"
                        title={getScreeningError(lead.id) || 'Retry screening'}
                      >
                        <AlertCircle className="w-3 h-3" />
                        Retry
                      </button>
                    ) : (
                      <button
                        onClick={(e) => handleScreenLead(lead.id, e)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gold bg-gold/10 border border-gold/30 rounded hover:bg-gold/20 transition-colors"
                      >
                        <Play className="w-3 h-3" />
                        Screen
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700 bg-slate-900">
        <div className="text-sm text-stone-500">
          {isLoading ? (
            <span>Loading...</span>
          ) : (
            <>
              Showing{' '}
              <span className="font-medium text-ivory">
                {leads.length > 0 ? (page - 1) * leads.length + 1 : 0}
              </span>
              {' - '}
              <span className="font-medium text-ivory">
                {Math.min(page * leads.length, total)}
              </span>{' '}
              of{' '}
              <span className="font-medium text-ivory">
                {total.toLocaleString()}
              </span>{' '}
              leads
              {selectedIds.size > 0 && (
                <span className="ml-4 text-gold font-medium">
                  ({selectedIds.size} selected)
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-stone-500">
            Page {page} of {pages || 1}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1 || isLoading}
              className="p-2 rounded border border-slate-700 hover:bg-gold/10 disabled:bg-slate-800 disabled:text-stone-400 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pages || isLoading}
              className="p-2 rounded border border-slate-700 hover:bg-gold/10 disabled:bg-slate-800 disabled:text-stone-400 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadResultsTable;
